import * as React from 'react';
import { memo } from 'react';

import styled from 'styled-components';

/**
 * Глубина владения навыком точками (ТЗ 0040, доводка вёрстки).
 *
 * Владелец открыл курс и спросил про число рядом с навыком: «что за число?
 * количество? степень глубины?». Это глубина владения по шкале 1–5 — свойство
 * ПАРЫ (курс, навык): что выпускник именно этого курса реально знает, а не
 * потолок темы. Голая цифра в скобках этого не сообщала никак.
 *
 * Рисунок перенесён из карточки участника карьерного центра
 * (`CareerCenter/parts/MemberProfilePanel`), где точки уже были и работали, —
 * чтобы одна и та же величина выглядела одинаково во всех местах.
 *
 * 🔴 **Атом не переводит сам.** Подпись приходит готовой строкой в `title`.
 * Локаль-гейт `careerCenter.locale.test.ts` сканирует фиксированный список
 * папок, и `atoms/` в него не входит: `t()` внутри атома молча выпал бы из
 * проверки паритета ru/pl. Слова собирает `careerSkillDepthTitle` в
 * `core/hooks/CareerCenter/careerModel.ts` — этот файл гейт проверяет.
 */
export interface ILevelDots {
  /** 1..max, зажимается. */
  value: number;
  /** Размер шкалы. По умолчанию 5 — как `SKILL_LEVEL_MAX` на бэкенде. */
  max?: number;
  /** Диаметр точки, px. */
  size?: number;
  /** Готовая подпись: «Уровень 4 из 5 · уверенно». Идёт в title и aria-label. */
  title: string;
}

export const LevelDots = memo(
  ({ value, max = 5, size = 6, title }: ILevelDots) => {
    // Зажимаем, а не доверяем: уровень приходит из БД, где на поле нет
    // валидатора — только клампы в сервисах. Шкала на экране не должна
    // разъезжаться из-за одной кривой строки.
    const filled = Math.max(0, Math.min(max, Math.round(value)));

    return (
      <Dots role="img" aria-label={title} title={title} $gap={size / 3}>
        {Array.from({ length: max }, (_, index) => (
          <Dot key={index} $filled={index < filled} $size={size} />
        ))}
      </Dots>
    );
  },
);

const Dots = styled.span<{ $gap: number }>`
  display: inline-flex;
  align-items: center;
  flex: none;
  gap: ${({ $gap }) => $gap}px;
`;

const Dot = styled.span<{ $filled: boolean; $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $filled }) =>
    $filled ? 'var(--k-bg-2563eb)' : 'var(--k-bg-dbe1ea)'};
`;
